import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { List, useDataGrid } from "@refinedev/mui";
import { Link as RouterLink } from "react-router";
import {
  ACCOUNT_DELETION_STATUSES,
  ACCOUNT_DELETIONS_RESOURCE,
  DELETION_STATUS_COLOR,
  DELETION_STATUS_LABEL,
  type AccountDeletionRecord,
} from "@/lib/account-deletion";

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

// A job moves on its own (the request starts it, a 15-minute sweep retries
// it), so the list follows it without a reload.
const REFRESH_INTERVAL_MS = 60_000;

/**
 * Every account deletion job, newest first (`GET admin/account-deletions`).
 * Read-only. The users page shows an open job on its account's row; this page
 * is for the rest — finished jobs, and a failed one whose account row is
 * already gone, which would otherwise be visible nowhere.
 */
export const AccountDeletionList = () => {
  const [status, setStatus] = React.useState<string>("");

  // The status entry is always present, `undefined` for "All": Refine keeps a
  // filter until an entry for the same field arrives with an empty value.
  const { dataGridProps } = useDataGrid<AccountDeletionRecord>({
    resource: ACCOUNT_DELETIONS_RESOURCE,
    filters: {
      permanent: [{ field: "status", operator: "eq" as const, value: status || undefined }],
    },
    queryOptions: {
      refetchInterval: REFRESH_INTERVAL_MS,
    },
  });

  const columns = React.useMemo<GridColDef<AccountDeletionRecord>[]>(
    () => [
      {
        field: "id",
        headerName: "Job",
        minWidth: 70,
        sortable: false,
        display: "flex",
      },
      {
        field: "userId",
        headerName: "User",
        minWidth: 110,
        sortable: false,
        display: "flex",
        // No FK: the `rows` step deletes the account, so a job past it — done,
        // or failed later on — names a user page that no longer exists. Only a
        // job that hasn't reached it links.
        renderCell: ({ row }) =>
          row.status !== "done" && !("rows" in (row.steps ?? {})) ? (
            <Link component={RouterLink} to={`/users/show/${row.userId}`} variant="body2">
              #{row.userId}
            </Link>
          ) : (
            <Typography variant="body2" color="text.secondary">
              #{row.userId}
            </Typography>
          ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 170,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={DELETION_STATUS_LABEL[row.status] ?? row.status}
            color={DELETION_STATUS_COLOR[row.status] ?? "default"}
            variant={row.status === "failed" ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "requestedBy",
        headerName: "Asked by",
        minWidth: 100,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => (row.requestedBy === "self" ? "the user" : "an admin"),
      },
      {
        field: "requestedAt",
        headerName: "Requested",
        minWidth: 190,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <Typography variant="body2">{formatDateTime(row.requestedAt)}</Typography>,
      },
      {
        field: "steps",
        headerName: "Steps done",
        flex: 1.2,
        minWidth: 220,
        sortable: false,
        display: "flex",
        // In the order they finished; the hover says when.
        renderCell: ({ row }) => {
          const steps = Object.entries(row.steps ?? {}).sort(([, a], [, b]) => a.localeCompare(b));
          if (steps.length === 0) {
            return (
              <Typography variant="body2" color="text.disabled">
                -
              </Typography>
            );
          }
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
              {steps.map(([step, at]) => (
                <Tooltip key={step} title={formatDateTime(at)}>
                  <Chip size="small" variant="outlined" label={step} />
                </Tooltip>
              ))}
            </Stack>
          );
        },
      },
      {
        field: "attempts",
        headerName: "Attempts",
        minWidth: 90,
        sortable: false,
        display: "flex",
      },
      {
        field: "lastError",
        headerName: "Last error",
        flex: 1,
        minWidth: 200,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) =>
          row.lastError ? (
            <Tooltip title={row.lastError}>
              <Typography variant="body2" color={row.status === "failed" ? "error" : "text.secondary"} noWrap>
                {row.lastError}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.disabled">
              -
            </Typography>
          ),
      },
      {
        field: "completedAt",
        headerName: "Completed",
        minWidth: 190,
        sortable: false,
        display: "flex",
        renderCell: ({ row }) => <Typography variant="body2">{formatDateTime(row.completedAt)}</Typography>,
      },
    ],
    []
  );

  return (
    <List canCreate={false} title="Account Deletions">
      <Box sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Every account deletion, newest first. A deletion is a background job: it waits while an evidence hold
          names the account, and is retried until it finishes or fails. A job outlives its account, so a failed one
          stays here after the user row is gone.
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {ACCOUNT_DELETION_STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {DELETION_STATUS_LABEL[value]}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <DataGrid {...dataGridProps} columns={columns} disableColumnFilter getRowHeight={() => "auto"} />
    </List>
  );
};
